/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { packetsChanged } from '@/api/invalidate';
import type { DefaultCopy } from '@/api/agreements';
import {
  MAX_BUNDLE_STEPS,
  MAX_COPY_BODY,
  MAX_COPY_SUBJECT,
  type PacketWorkflow,
  createBundle,
  packetWorkflowsQuery,
  removeBundle,
  startPacket,
  updateBundle,
} from '@/api/packets';
import { workflowsQuery } from '@/api/workflows';
import { AddressList } from '@/components/SendCopy';
import { Icon } from '@/components/ui/Icon';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { Modal } from '@/components/ui/Modal';
import { Empty, Failed, Rows } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { LOOKS_LIKE_EMAIL, NO_AUTOFILL } from '@/lib/agreementFields';
import { nameProblem, tidyName } from '@/lib/counterparties';
import { BUNDLE_STEP_DELETED } from '@/lib/deletedTemplates';
import { useLeaveGuard } from '@/lib/leaveGuard';
import { ApiError } from '@/lib/queryClient';

/**
 * The packets a person can start: the named, ordered sets of workflows one
 * person signs. (The API calls one a packet workflow.)
 *
 * A started packet copies its steps, so editing one here changes only packets
 * started afterwards (`PKT-1`). Managing is for whoever manages workflows; the
 * server decides, and hiding the buttons is UX only.
 */
export function PacketCards({
  canManage,
  onNew,
  onEdit,
  onRetire,
}: {
  canManage: boolean;
  onNew: () => void;
  onEdit: (bundle: PacketWorkflow) => void;
  onRetire: (bundle: PacketWorkflow) => void;
}) {
  const { data, isPending, isError, error, refetch } = useQuery(packetWorkflowsQuery());

  if (isPending) {
    return (
      <div className="card">
        <Rows rows={3} />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="card">
        <Failed error={error} onRetry={() => void refetch()} />
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div className="card">
        <Empty
          icon="inbox"
          title="No packets yet"
          detail="A packet is the set of agreements one person signs, in order."
          action={
            canManage ? (
              <button type="button" className="btn btn-q btn-sm" onClick={onNew}>
                <Icon name="plus" size={14} />
                New packet
              </button>
            ) : null
          }
        />
      </div>
    );
  }

  return (
    <div className="pkgrid">
      {data.map((bundle) => (
        <section className="card pkcard" key={bundle.id}>
          <div className="pkcard-top">
            <span className="pkcard-ico" aria-hidden>
              <Icon name="inbox" size={18} />
            </span>
            <div className="pkcard-name">
              <h2>{bundle.name}</h2>
              <span className="pkcard-meta">
                {bundle.steps.length} {bundle.steps.length === 1 ? 'step' : 'steps'}
                {bundle.packet_count > 0
                  ? ` · ${String(bundle.packet_count)} started`
                  : ' · not started yet'}
              </span>
            </div>
          </div>

          {bundle.description ? (
            <p className="pkcard-desc">{bundle.description}</p>
          ) : null}

          {/* A step's template was deleted: it starts no packet (ADW-5498). */}
          {bundle.step_workflows.some((step) => step.template_retired) ? (
            <p className="warn-line" role="status">
              <Icon name="warn" size={14} />
              {BUNDLE_STEP_DELETED}
            </p>
          ) : null}

          <ol className="pkpath" aria-label={`Steps in ${bundle.name}`}>
            {bundle.steps.map((step, index) => (
              <li key={`${String(index)}-${step}`}>
                <span className="n">{index + 1}</span>
                {step}
              </li>
            ))}
          </ol>

          {/* Setting a packet up, not starting one: that is New in the top bar. */}
          <div className="pkcard-actions">
            {canManage ? (
              <button
                type="button"
                className="btn btn-q"
                aria-label={`Edit ${bundle.name}`}
                onClick={() => {
                  onEdit(bundle);
                }}
              >
                <Icon name="pen" size={14} />
                Edit
              </button>
            ) : null}
            {canManage ? (
              <Menu
                label={`Options for ${bundle.name}`}
                trigger={(props) => (
                  <button
                    type="button"
                    className="btn btn-t btn-sm"
                    {...props}
                    aria-label={`Options for ${bundle.name}`}
                  >
                    <Icon name="kebab" size={15} />
                  </button>
                )}
              >
                {(close) => (
                  <>
                    <MenuItem
                      icon={<Icon name="pen" size={15} />}
                      title="Edit"
                      onClick={() => {
                        close();
                        onEdit(bundle);
                      }}
                    />
                    <MenuItem
                      icon={<Icon name="x" size={15} />}
                      title="Retire"
                      onClick={() => {
                        close();
                        onRetire(bundle);
                      }}
                    />
                  </>
                )}
              </Menu>
            ) : null}
          </div>
        </section>
      ))}
      {canManage ? (
        <button type="button" className="pkcard-new" onClick={onNew}>
          <span className="pkcard-ico" aria-hidden>
            <Icon name="plus" size={18} />
          </span>
          <b>Create another packet</b>
          <small>Choose the agreements one person signs, in order.</small>
        </button>
      ) : null}
    </div>
  );
}

/**
 * Starting a packet for one person. The card chose which packet; all that is
 * left to ask is who signs, and where their signature requests go (`PKT-15`).
 */
export function StartBundle({
  bundle,
  onClose,
}: {
  bundle: PacketWorkflow;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const emailOk = LOOKS_LIKE_EMAIL.test(email.trim());
  // Their name becomes a counterparty, so it is held to what a name may be
  // (VL-2044), said once the field is left.
  const [nameLeft, setNameLeft] = useState(false);
  const nameRefused = nameProblem(name);

  const start = useMutation({
    mutationFn: () =>
      startPacket({
        packet_workflow_id: bundle.id,
        subject_name: tidyName(name),
        subject_email: email.trim(),
      }),
    onSuccess: async (packet) => {
      // Straight to its checklist, where its first step is started (`PKT-16`).
      // Before closing: closing unmounts the dialog that asked.
      await navigate({ to: '/packets/$packetId', params: { packetId: packet.id } });
      toast(`${bundle.name} started for ${tidyName(name)}.`);
      onClose();
      await packetsChanged(client);
    },
  });

  const failed = start.error instanceof ApiError ? start.error : undefined;

  return (
    <Modal
      title={`Start ${bundle.name}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-p"
            disabled={!name.trim() || nameRefused !== null || !emailOk || start.isPending}
            onClick={() => {
              start.mutate();
            }}
          >
            {start.isPending ? 'Starting…' : 'Start'}
          </button>
          <button type="button" className="btn btn-q" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className="pane p-0!">
        <div className="field">
          <label htmlFor="pk-name">
            Person&apos;s name <span className="req">*</span>
          </label>
          <input
            className="inp"
            id="pk-name"
            value={name}
            maxLength={255}
            placeholder="Their full name"
            aria-invalid={Boolean(nameLeft && nameRefused)}
            aria-describedby={nameLeft && nameRefused ? 'pk-name-err' : undefined}
            onChange={(event) => {
              setName(event.target.value);
            }}
            onBlur={() => {
              setNameLeft(true);
            }}
          />
          {nameLeft && nameRefused ? (
            <span className="err" id="pk-name-err">
              {nameRefused}
            </span>
          ) : null}
        </div>
        <div className="field mt-lg!">
          <label htmlFor="pk-email">
            Their email <span className="req">*</span>
          </label>
          <input
            className="inp"
            id="pk-email"
            type="email"
            {...NO_AUTOFILL}
            value={email}
            maxLength={320}
            placeholder="name@example.com"
            aria-invalid={touched && !emailOk}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            onBlur={() => {
              setTouched(true);
            }}
          />
          {touched && email.trim() && !emailOk ? (
            <span className="err">That does not look like an email address.</span>
          ) : null}
        </div>
        {failed ? (
          <p className="warn-line" role="alert">
            <Icon name="warn" size={14} />
            {failed.detail || 'It could not be started.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

type Step = { id: string; name: string; copy: DefaultCopy };

const NO_COPY: DefaultCopy = { to: [], subject: null, body: null };

/** Whether a step's default copy says anything at all. */
function hasCopy(copy: DefaultCopy): boolean {
  return (
    copy.to.length > 0 || Boolean(copy.subject?.trim()) || Boolean(copy.body?.trim())
  );
}

/** What a packet template says, in a form two can be compared in. */
function shapeOf(name: string, description: string, steps: Step[]): string {
  return JSON.stringify({
    name: name.trim(),
    description: description.trim(),
    steps: steps.map((s) => ({
      id: s.id,
      to: s.copy.to,
      subject: s.copy.subject?.trim() ?? '',
      body: s.copy.body?.trim() ?? '',
    })),
  });
}

/**
 * Creating or editing a packet: a name, and the workflows in order.
 *
 * Two actions and no others, as on an agreement template
 * (`packets/spec.md` §3.6): **Publish** sends every change at once, and
 * **Cancel** -- or the close button, or Escape -- throws them away, asking
 * first if there are any. The changes are held here until Publish: a packet
 * copies its steps when it starts (`PKT-1`), so nothing running can see a
 * half-made edit, and leaving with changes warns.
 *
 * Reordering is Move up and Move down rather than dragging, so it works from
 * the keyboard with nothing extra to learn.
 */
export function BundleEditor({
  bundle,
  onClose,
}: {
  bundle: PacketWorkflow | null;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const workflows = useQuery(workflowsQuery());
  const [name, setName] = useState(bundle?.name ?? '');
  const [description, setDescription] = useState(bundle?.description ?? '');
  const [steps, setSteps] = useState<Step[]>(
    bundle?.step_workflows.map((s) => ({
      id: s.workflow_id,
      name: s.workflow_name,
      copy: s.default_copy ?? NO_COPY,
    })) ?? [],
  );
  const [confirming, setConfirming] = useState(false);
  // What it said when the editor opened, to tell whether anything changed.
  const [opened] = useState(() => shapeOf(name, description, steps));

  const setCopy = (id: string, change: Partial<DefaultCopy>) => {
    setSteps((current) =>
      current.map((s) => (s.id === id ? { ...s, copy: { ...s.copy, ...change } } : s)),
    );
  };

  const changed = shapeOf(name, description, steps) !== opened;
  const guard = useLeaveGuard({
    when: changed,
    message: 'Your changes to this packet are not published yet. Leave and lose them?',
  });

  // Held still, because the dialog moves focus whenever its close changes.
  const close = useRef(onClose);
  const changedNow = useRef(changed);
  useEffect(() => {
    close.current = onClose;
    changedNow.current = changed;
  }, [onClose, changed]);
  const requestClose = useCallback(() => {
    if (changedNow.current) setConfirming(true);
    else close.current();
  }, []);
  const keepEditing = useCallback(() => {
    setConfirming(false);
  }, []);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        workflow_ids: steps.map((s) => s.id),
        default_copies: steps
          .filter((s) => hasCopy(s.copy))
          .map((s) => ({
            workflow_id: s.id,
            to: s.copy.to,
            subject: s.copy.subject?.trim() || null,
            body: s.copy.body?.trim() ? s.copy.body : null,
          })),
      };
      return bundle ? updateBundle(bundle.id, body) : createBundle(body);
    },
    onSuccess: async (saved) => {
      guard.release();
      await client.invalidateQueries({ queryKey: ['packet-workflows'] });
      onClose();
      toast(bundle ? `${saved.name} is published.` : `${saved.name} is ready to start.`);
    },
  });

  const chosen = new Set(steps.map((s) => s.id));
  const available = (workflows.data ?? []).filter((w) => !chosen.has(w.id));
  const full = steps.length >= MAX_BUNDLE_STEPS;
  const copiesOk = steps.every((s) => s.copy.to.every((a) => LOOKS_LIKE_EMAIL.test(a)));
  const ready = name.trim().length > 0 && steps.length > 0 && copiesOk;
  const failed = save.error instanceof ApiError ? save.error : undefined;
  const missing = !name.trim()
    ? 'Give it a name to publish it.'
    : steps.length === 0
      ? 'Add at least one workflow to publish it.'
      : !copiesOk
        ? 'Every default copy address has to be an email address.'
        : null;

  const move = (index: number, by: -1 | 1) => {
    setSteps((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (item) next.splice(index + by, 0, item);
      return next;
    });
  };

  if (confirming) {
    return (
      <Modal
        title="Discard your changes?"
        onClose={keepEditing}
        footer={
          <>
            <button
              type="button"
              className="btn btn-d"
              onClick={() => {
                guard.release();
                onClose();
              }}
            >
              Discard changes
            </button>
            <button type="button" className="btn btn-q" onClick={keepEditing}>
              Keep editing
            </button>
          </>
        }
      >
        <p className="mut m-0!">
          {bundle
            ? `Everything you changed in ${bundle.name} goes, and it stays as it was.`
            : 'This packet has not been published, so nothing is created.'}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={bundle ? 'Edit packet' : 'New packet'}
      onClose={requestClose}
      footer={
        <>
          {changed && missing ? (
            <small className="mut foot-aside">{missing}</small>
          ) : null}
          <button
            type="button"
            className="btn btn-p"
            disabled={!changed || !ready || save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            {save.isPending ? 'Publishing…' : 'Publish'}
          </button>
          <button type="button" className="btn btn-q" onClick={requestClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className="pane new-tpl">
        <p className="pk-lede">
          A packet is the set of agreements one person signs, one after another. Packets
          already started keep the steps they started with.
        </p>

        <div className="field">
          <label htmlFor="b-name">
            Name <span className="req">*</span>
          </label>
          <input
            className="inp"
            id="b-name"
            value={name}
            maxLength={255}
            placeholder="New joiner onboarding"
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label htmlFor="b-desc">
            Description <span className="mut">(optional)</span>
          </label>
          <input
            className="inp"
            id="b-desc"
            value={description}
            maxLength={500}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <span className="flabel" id="b-steps">
            Steps, in the order they are signed <span className="req">*</span>
          </span>
          {steps.length === 0 ? (
            <p className="blist-none">
              Nothing yet. Add the first agreement below; the order you add them in is the
              order they are signed.
            </p>
          ) : (
            <ol className="blist" aria-labelledby="b-steps">
              {steps.map((step, index) => (
                <li key={step.id}>
                  <span className="n">{index + 1}</span>
                  <span>{step.name}</span>
                  <span className="brow-actions">
                    <button
                      type="button"
                      className="btn btn-t btn-sm"
                      aria-label={`Move ${step.name} up`}
                      disabled={index === 0}
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      <Icon name="rise" size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-t btn-sm"
                      aria-label={`Move ${step.name} down`}
                      disabled={index === steps.length - 1}
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      <Icon name="fall" size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-t btn-sm"
                      aria-label={`Remove ${step.name}`}
                      onClick={() => {
                        setSteps((current) => current.filter((s) => s.id !== step.id));
                      }}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </span>
                  <details className="bcopy">
                    <summary>
                      Default copy
                      {hasCopy(step.copy)
                        ? ` · ${String(step.copy.to.length)} ${step.copy.to.length === 1 ? 'address' : 'addresses'}`
                        : ''}
                    </summary>
                    <div className="stack">
                      <div className="field">
                        <label htmlFor={`bc-to-${step.id}`}>To</label>
                        <AddressList
                          id={`bc-to-${step.id}`}
                          value={step.copy.to}
                          onChange={(to) => {
                            setCopy(step.id, { to });
                          }}
                        />
                      </div>
                      <div className="field">
                        <label htmlFor={`bc-subject-${step.id}`}>Subject</label>
                        <input
                          className="inp"
                          id={`bc-subject-${step.id}`}
                          {...NO_AUTOFILL}
                          value={step.copy.subject ?? ''}
                          maxLength={MAX_COPY_SUBJECT}
                          placeholder={`${step.name} — signed`}
                          onChange={(event) => {
                            setCopy(step.id, { subject: event.target.value });
                          }}
                        />
                      </div>
                      <div className="field">
                        <label htmlFor={`bc-body-${step.id}`}>Message</label>
                        <textarea
                          className="inp"
                          id={`bc-body-${step.id}`}
                          rows={3}
                          value={step.copy.body ?? ''}
                          maxLength={MAX_COPY_BODY}
                          onChange={(event) => {
                            setCopy(step.id, { body: event.target.value });
                          }}
                        />
                      </div>
                    </div>
                  </details>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="field">
          <span className="flabel">Add a workflow</span>
          {workflows.isPending ? <Rows rows={2} /> : null}
          {workflows.isError ? (
            <p className="warn-line" role="alert">
              <Icon name="warn" size={14} />
              The workflows could not be loaded.
            </p>
          ) : null}
          {workflows.data && available.length === 0 ? (
            <p className="mut">Every workflow is in this packet.</p>
          ) : null}
          {available.length > 0 ? (
            <div className="wrap-row blist-add">
              {available.map((workflow) => (
                <button
                  key={workflow.id}
                  type="button"
                  className="chip whitespace-nowrap!"
                  aria-label={`Add ${workflow.name}`}
                  disabled={full}
                  onClick={() => {
                    setSteps((current) => [
                      ...current,
                      { id: workflow.id, name: workflow.name, copy: NO_COPY },
                    ]);
                  }}
                >
                  <Icon name="plus" size={12} />
                  <span>{workflow.name}</span>
                </button>
              ))}
            </div>
          ) : null}
          {full ? (
            <small className="mut hint-below">
              A packet holds at most {MAX_BUNDLE_STEPS} workflows.
            </small>
          ) : null}
        </div>

        {failed ? (
          <p className="warn-line" role="alert">
            <Icon name="warn" size={14} />
            {failed.detail || 'It could not be published.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Retiring a packet.
 *
 * One never started is deleted; one that was started is retired, so it is no
 * longer offered and everything in progress carries on. The server decides.
 */
export function RetireBundle({
  bundle,
  onClose,
}: {
  bundle: PacketWorkflow;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const used = bundle.packet_count > 0;

  const retire = useMutation({
    mutationFn: () => removeBundle(bundle.id),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: ['packet-workflows'] });
      onClose();
      toast(
        result.outcome === 'deleted'
          ? `${result.name} is gone.`
          : `${result.name} is retired.`,
      );
    },
  });

  const failed = retire.error instanceof ApiError ? retire.error : undefined;

  return (
    <Modal
      title="Retire this packet"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-d"
            disabled={retire.isPending}
            onClick={() => {
              retire.mutate();
            }}
          >
            {retire.isPending ? 'Retiring…' : 'Retire it'}
          </button>
          <button type="button" className="btn btn-q" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className="pane p-0!">
        <p>
          <b>{bundle.name}</b>
        </p>
        <p className="mut">
          {used
            ? `It will no longer be offered. ${String(bundle.packet_count)} in progress will carry on.`
            : 'It will no longer be offered.'}
        </p>
        {failed ? (
          <p className="warn-line" role="alert">
            <Icon name="warn" size={14} />
            {failed.detail || 'It could not be retired.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
